import { gql, useMutation } from "@apollo/client";
import React, { Dispatch, SetStateAction, useCallback, useMemo, useState } from "react";
import { loggedInUser } from "src/apollo/cache";
import { activity, bell, inbox, xIcon } from "src/images/NewDesign";
import { useDebounce } from "src/utils/hooks";
import { theme } from "src/utils/theme";
import styled, { keyframes } from "styled-components";
import { AppText, FlexDiv } from "../UI";
import { PhoenixAppButton, PhoenixIcon, PhoenixInput } from "../UI/Phoenix";
import { TabType } from "./ActivityCenterModule";
import { InboxList } from "./InboxList";
import { NotificationList } from "./NotificationList";
import { RecentActivityList } from "./RecentActivityList";

const MARK_ALL_AS_READ = gql`
  mutation markNotificationsAsRead {
    markNotificationsAsRead
  }
`;

interface IActivityCenterModalProps {
  selectedTab: TabType;
  setSelectedTab: React.Dispatch<React.SetStateAction<TabType>>;
  setShowActivityCenterModal: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ActivityCenterModal: React.FC<IActivityCenterModalProps> = ({
  selectedTab,
  setSelectedTab,
  setShowActivityCenterModal,
}) => {
  const isRep = useMemo(() => ["AE", "SDR"].includes(loggedInUser()?.role || ""), []);

  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [search, setSearch] = useState<string>("");

  const [hoistedSetNotifications, setHoistedSetNotifications] = useState<Function>(() => () => {});

  useDebounce(
    () => {
      setDebouncedSearch(search);
    },
    [search],
    500,
  );

  const [markNotificationsAsRead, { loading: loadingMarkAllAsRead }] = useMutation(MARK_ALL_AS_READ, {
    onError({ message }) {
      console.log("Error in markNotificationsAsRead: ", message);
    },
  });

  const handleMarkAllAsRead = useCallback(() => {
    markNotificationsAsRead();
    hoistedSetNotifications((prevNotifications: any[]) =>
      prevNotifications.map((notification) => ({
        ...notification,
        read: true,
      })),
    );
  }, [hoistedSetNotifications, markNotificationsAsRead]);

  return (
    <>
      <DarkDiv />
      <ModalContainer direction="column">
        <ModalHeader>
          <PhoenixIcon svg={xIcon} size={24} pointer onClick={() => setShowActivityCenterModal(false)} />
          <AppText fontSize={16} fontWeight={500} lineHeight={22} textAlign="center" style={{ width: "100%" }}>
            Activity Center
          </AppText>
        </ModalHeader>

        <TabsContainer>
          <BorderFill />
          {isRep && <Tab selected={selectedTab === "activity"} type="activity" setSelectedTab={setSelectedTab} />}
          <Tab selected={selectedTab === "inbox"} type="inbox" setSelectedTab={setSelectedTab} />
          <Tab selected={selectedTab === "notifications"} type="notifications" setSelectedTab={setSelectedTab} />
          <BorderFill />
        </TabsContainer>

        <FlexDiv gap={8} padding="16px 16px 8px 16px" direction="column">
          {!["notifications", "activity"].includes(selectedTab) && (
            <PhoenixInput
              placeholder="Search Leads"
              variant="primary"
              type="text"
              searchInput
              fontSize={12}
              fontWeight={400}
              displayNoContextText
              value={search}
              onChange={(e: any) => setSearch(e.target.value)}
              height={40}
            />
          )}
          <FlexDiv justify="space-between" align="center" padding="0px 8px">
            {!["inbox", "activity"].includes(selectedTab) && (
              <PhoenixAppButton
                variant="brand-outline"
                buttonType="ghost-small"
                onClick={handleMarkAllAsRead}
                disabled={loadingMarkAllAsRead}
                style={{ marginLeft: "auto" }}
              >
                Mark All as Read
              </PhoenixAppButton>
            )}
          </FlexDiv>
        </FlexDiv>

        <Body>
          {selectedTab === "activity" && <RecentActivityList />}
          {selectedTab === "inbox" && <InboxList debouncedSearch={debouncedSearch} />}
          {selectedTab === "notifications" && (
            <NotificationList setHoistedSetNotifications={setHoistedSetNotifications} />
          )}
        </Body>
      </ModalContainer>
    </>
  );
};

const slideIn = keyframes`
  0% {
    width: 0px;
  }
  100% {
    width: 456px;
  }
`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const darkDivFadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 0.5;
  }
`;

const ModalContainer = styled(FlexDiv)`
  position: fixed;
  z-index: 1001;
  top: 0;
  right: 0;

  width: 520px;
  height: 100vh;

  background-color: ${theme.fill.neutral.primary};

  border-left: 1px solid ${theme.border.neutral.primary};

  animation: ${slideIn} 0.4s ease forwards;
  & > * {
    animation: ${fadeIn} 0.75s ease forwards;
  }
`;
const DarkDiv = styled.div`
  position: fixed;
  z-index: 1000;
  left: 0;

  width: 100vw;
  height: 100vh;

  background: ${theme.surface.brand.inverse};
  opacity: 0.5;

  animation: ${darkDivFadeIn} 0.75s ease forwards;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;

  height: 56px;
  padding: 24px 16px 8px 16px;
`;

const TabsContainer = styled.div`
  display: flex;
  min-height: 32px;
  max-height: 32px;
`;
const BorderFill = styled.div`
  width: 100%;
  border-bottom: 1px solid ${theme.border.neutral.primary};
`;

const Body = styled.div`
  height: 100%;
`;

const Tab: React.FC<{ selected: boolean; type: TabType; setSelectedTab: Dispatch<SetStateAction<TabType>> }> = ({
  selected,
  type,
  setSelectedTab,
}) => {
  const icon = useMemo(() => {
    switch (type) {
      case "activity":
        return activity;
      case "inbox":
        return inbox;
      case "notifications":
        return bell;
      default:
        return activity;
    }
  }, [type]);

  const title = useMemo(() => {
    switch (type) {
      case "activity":
        return "Recent Activity";
      case "inbox":
        return "Inbox";
      case "notifications":
        return "Notifications";
      default:
        return "Recent Activity";
    }
  }, [type]);

  return (
    <TabContainer
      align="center"
      gap={8}
      padding="0px 8px"
      style={{
        borderBottom: selected
          ? `2px solid ${theme.border.brand.primary}`
          : `1px solid ${theme.border.neutral.primary}`,
      }}
      onClick={() => setSelectedTab(type)}
    >
      <PhoenixIcon
        svg={icon}
        size={16}
        color={theme.icon.neutral.default}
        hoverColor={theme.icon.neutral.default}
        pointer
      />
      <AppText
        fontSize={10}
        fontWeight={600}
        lineHeight={16}
        letterSpacing={1}
        uppercase
        noWrap
        color={selected ? theme.text.neutral.primary : theme.text.neutral.secondary}
      >
        {title}
      </AppText>
    </TabContainer>
  );
};
const TabContainer = styled(FlexDiv)`
  transition: border-bottom 0.1s ease-in-out;
  cursor: pointer;

  & > p {
    user-select: none;
  }
`;
